import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  IconButton,
  Flex,
  Text,
  Input,
  VStack,
  HStack,
  Spinner,
} from "@chakra-ui/react";
import { FiMessageSquare, FiX, FiSend, FiTrash2 } from "react-icons/fi";
import { supabase } from "../../lib/supabaseClient";
import { useUser } from "../../contexts/UserContext";
import ChatMessage from "./ChatMessage";
import { tt } from "../../i18n/tt";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { userInfo } = useUser();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMessage = { role: "user", content: trimmed };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.access_token) {
        throw new Error("로그인이 필요합니다");
      }

      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/chat-with-claude`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            message: trimmed,
            history: history,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `서버 오류 (${response.status})`);
      }

      const data = await response.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply },
      ]);
      setHistory(data.history || []);
    } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `오류가 발생했습니다: ${error.message}`,
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setHistory([]);
  };

  return (
    <>
      {/* 우측 고정 탭 버튼 */}
      {!isOpen && (
        <Box
          as="button"
          onClick={() => setIsOpen(true)}
          position="fixed"
          right="0"
          top="50%"
          transform="translateY(-50%)"
          zIndex={1400}
          bg="brand.500"
          color="white"
          px="6px"
          py="16px"
          borderLeftRadius="8px"
          shadow="lg"
          cursor="pointer"
          _hover={{ bg: "brand.600", px: "10px" }}
          transition="all 0.2s"
          style={{ writingMode: "vertical-rl" }}
        >
          <Flex alignItems="center" gap="6px" fontSize="13px" fontWeight="bold">
            <FiMessageSquare size={16} />
            {tt("a.a9a04807", "AI 어시스턴트")}
          </Flex>
        </Box>
      )}

      {/* 우측 사이드 패널 */}
      <Box
        position="fixed"
        top="0"
        right={isOpen ? "0" : { base: "-100vw", md: "-420px" }}
        w={{ base: "100vw", md: "420px" }}
        maxW="420px"
        h="100vh"
        bg="white"
        zIndex={1500}
        shadow={isOpen ? "-4px 0 24px rgba(0,0,0,0.12)" : "none"}
        transition="right 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        display="flex"
        flexDirection="column"
        borderLeft="1px solid"
        borderColor="gray.200"
      >
        {/* 헤더 */}
        <Flex
          bg="brand.500"
          color="white"
          px={4}
          py={3}
          pt="max(12px, env(safe-area-inset-top))"
          alignItems="center"
          justifyContent="space-between"
          flexShrink={0}
        >
          <HStack spacing={2}>
            <FiMessageSquare size={18} />
            <Text fontWeight="bold" fontSize="sm">
              {tt("a.50b4d539", "Laeebly AI 어시스턴트")}
            </Text>
          </HStack>
          <HStack spacing={1}>
            <IconButton
              icon={<FiTrash2 size={14} />}
              size="xs"
              variant="ghost"
              color="white"
              _hover={{ bg: "brand.600" }}
              onClick={clearChat}
              aria-label={tt("a.118aa348", "대화 초기화")}
              title={tt("a.118aa348", "대화 초기화")}
            />
            <IconButton
              icon={<FiX size={18} />}
              size="xs"
              variant="ghost"
              color="white"
              _hover={{ bg: "brand.600" }}
              onClick={() => setIsOpen(false)}
              aria-label={tt("a.218e2ae6", "닫기")}
            />
          </HStack>
        </Flex>

        {/* 메시지 영역 */}
        <VStack
          flex={1}
          overflowY="auto"
          px={4}
          py={3}
          spacing={3}
          alignItems="stretch"
          bg="gray.50"
          css={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              background: "#CBD5E0",
              borderRadius: "3px",
            },
          }}
        >
          {messages.length === 0 && (
            <Box textAlign="center" py={8}>
              <Text fontSize="2xl" mb={3}>
                👋
              </Text>
              <Text fontSize="sm" color="gray.500" fontWeight="medium">{tt("a.fb6e7da2", "안녕하세요, {{v0}}님!", { v0: userInfo?.name || "운영팀" })}</Text>
              <Text fontSize="xs" color="gray.400" mt={1}>
                {tt("a.840bf4db", "채널 수익, 정산 현황, 영상 데이터 등을 질문해보세요.")}
              </Text>
              <VStack mt={4} spacing={2}>
                {[
                  "이번 달 전체 채널 수익 현황",
                  "정산 대기 중인 건수",
                  "최근 등록된 채널 목록",
                  "이번 달 쇼핑 매출 현황",
                ].map((suggestion) => (
                  <Box
                    key={suggestion}
                    as="button"
                    px={3}
                    py={2}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    fontSize="xs"
                    color="gray.600"
                    cursor="pointer"
                    w="100%"
                    textAlign="left"
                    _hover={{ bg: "brand.50", borderColor: "brand.300" }}
                    onClick={() => {
                      setInput(suggestion);
                      setTimeout(() => inputRef.current?.focus(), 100);
                    }}
                  >
                    {suggestion}
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          {messages.map((msg, idx) => (
            <ChatMessage key={idx} message={msg} />
          ))}

          {isLoading && (
            <HStack
              alignSelf="flex-start"
              bg="white"
              px={4}
              py={3}
              borderRadius="lg"
              shadow="sm"
            >
              <Spinner size="sm" color="brand.400" />
              <Text fontSize="xs" color="gray.500">
                {tt("a.f354823c", "데이터를 조회하고 있습니다...")}
              </Text>
            </HStack>
          )}

          <div ref={messagesEndRef} />
        </VStack>

        {/* 입력 영역 */}
        <Flex
          px={3}
          py={3}
          pb="max(12px, env(safe-area-inset-bottom))"
          bg="white"
          borderTop="1px solid"
          borderColor="gray.100"
          flexShrink={0}
        >
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={tt("a.cf8bc020", "질문을 입력하세요...")}
            size="sm"
            borderRadius="full"
            bg="gray.50"
            border="1px solid"
            borderColor="gray.200"
            _focus={{
              borderColor: "brand.400",
              bg: "white",
              boxShadow: "0 0 0 1px #4299E1",
            }}
            mr={2}
            disabled={isLoading}
          />
          <IconButton
            icon={<FiSend size={16} />}
            size="sm"
            borderRadius="full"
            bg="brand.500"
            color="white"
            _hover={{ bg: "brand.600" }}
            _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
            onClick={sendMessage}
            isDisabled={!input.trim() || isLoading}
            aria-label={tt("a.f235c8d9", "전송")}
          />
        </Flex>
      </Box>

      {/* 오버레이 (패널 열렸을 때 배경 클릭으로 닫기) */}
      {isOpen && (
        <Box
          position="fixed"
          top="0"
          left="0"
          w="100vw"
          h="100vh"
          bg="blackAlpha.300"
          zIndex={1499}
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default ChatWidget;
